import { useQuery } from '@tanstack/react-query'

import { getPatientSchedules } from 'services/patients/getPatientSchedules'
import { SCHEDULED } from 'settings/_scheduleStatusSettings'

interface PatientSchedulesParams {
  patientUsername: string
  startDate: string
  endDate: string
  shouldRequest: boolean
  scheduleType: 'PSIC' | 'PSIQ' | null
}

interface Schedule {
  startDate: string
  endDate: string
}

interface UseGetPatientSchedules {
  patientSchedulesForDayPicker: Date[]
  isLoadingPatientSchedules: boolean
}

export function useGetPatientSchedules({ 
  patientUsername, 
  startDate,
  endDate, 
  shouldRequest,
  scheduleType
}: PatientSchedulesParams): UseGetPatientSchedules {
  const pagination = { page: 1, perPage: 99 }
  const statusCodeList = [SCHEDULED]
  
  const { data, isError, fetchStatus } = useQuery({
    queryKey: ['PATIENTS_SCHEDULES', patientUsername, startDate],
    queryFn: () => getPatientSchedules({ 
      patientUsername, 
      startDate,
      endDate, 
      pagination, 
      statusCodeList,
      scheduleType
    }),
    enabled: shouldRequest,
    staleTime: 0,
    cacheTime: 0
  })

  if (!data || isError) {
    return {
      patientSchedulesForDayPicker: [],
      isLoadingPatientSchedules: fetchStatus === 'fetching'
    }
  }

  const formattedData = {
    schedules: data.schedules.map((schedule) => ({
      startDate: schedule.start_date,
      endDate: schedule.end_date
    }))
  }
  
  const formattedDataForDayPicker = formattedData.schedules.map((schedule: Schedule) => {
    const newStartDate = new Date(schedule.startDate)
    const timezoneOffset = newStartDate.getTimezoneOffset()
    const ONE_MINUTE_IN_MILLISECONDS = 60000
    return new Date(newStartDate.getTime() - (timezoneOffset * ONE_MINUTE_IN_MILLISECONDS))
  })
  
  return {
    patientSchedulesForDayPicker: formattedDataForDayPicker,
    isLoadingPatientSchedules: fetchStatus === 'fetching'
  }
}